import { useEffect } from 'preact/hooks'
import { Devices } from '../devices/devices.tsx'
import { ConsoleOverlay } from '../devtools/console-overlay.tsx'
import { StatsOverlay } from '../devtools/stats-overlay.tsx'
import { Eyelids } from '../hud/eyelids.tsx'
import { FpsCounter } from '../hud/fps-counter.tsx'
import { Hud } from '../hud/hud.tsx'
import { SaveToast } from '../hud/save-toast.tsx'
import type { I18n } from '../i18n/index.ts'
import { I18nContext } from '../i18n/index.ts'
import { RotateDevicePrompt } from '../screens/rotate-device-prompt.tsx'
import { TouchControls } from '../touch/touch-controls.tsx'
import type { ClientBridge } from './client-context.ts'
import { ClientContext } from './client-context.ts'
import { ScreenRouter } from './screen-router.tsx'
import { useView } from './use-view.ts'

interface AppProps {
  readonly bridge: ClientBridge
  readonly i18n: I18n
}

/** Follows the reduced-motion setting. The OS media query also zeros durations. */
function ReducedMotion() {
  const reducedMotion = useView((view) => view.settings.accessibility.reducedMotion)
  useEffect(() => {
    document.documentElement.toggleAttribute('data-reduced-motion', reducedMotion)
  }, [reducedMotion])
  return null
}

export function App({ bridge, i18n }: AppProps) {
  return (
    <ClientContext.Provider value={bridge}>
      <I18nContext.Provider value={i18n}>
        <ReducedMotion />
        <div class="ui">
          {/* Click-through: HUD elements opt in to pointer events themselves. */}
          <div class="layer-hud">
            <Devices />
            <TouchControls />
            <Hud />
            {/* Over the HUD: asleep, the screen is black. */}
            <Eyelids />
            {/* Over the eyelids: a night's save happens in the dark. */}
            <SaveToast />
            <FpsCounter />
          </div>
          <div class="layer-screens">
            <ScreenRouter />
          </div>
          {/* Overlays above everything: the rotate-device prompt, later the console. */}
          <div class="layer-system">
            <RotateDevicePrompt />
            <StatsOverlay />
            <ConsoleOverlay />
          </div>
        </div>
      </I18nContext.Provider>
    </ClientContext.Provider>
  )
}
